/**
 * LeagueGamesCard — the team's league games on the team side
 * (2026-09-28).
 *
 * League games are scheduled by the league (home + away), so they are
 * not part of the team's own game list. This card lists the next few
 * and the latest results for every league the team plays in:
 *   • coaches / managers → the league game sheet (lineups, scoring,
 *     sign-off)
 *   • everyone else → the public game page, when the league's site is on
 * Renders nothing for a team that isn't in a league.
 *
 * Backend: GET /api/teams/:teamId/league-games (team-league-games.js).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, ChevronDown, ChevronUp, MapPin } from 'lucide-react';
import api from '../services/api.js';

function fmtDate(d) {
  if (!d) return '';
  try {
    return new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  } catch { return d; }
}
function fmtTime(t) {
  if (!t) return '';
  const [h, m] = String(t).split(':').map(Number);
  if (!Number.isFinite(h)) return '';
  return `${((h + 11) % 12) + 1}:${String(m || 0).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

export default function LeagueGamesCard({ teamId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem('gd.leagueGamesCard.open') !== '0'; } catch { return true; }
  });

  useEffect(() => {
    if (!teamId) return;
    let cancelled = false;
    api.get(`/teams/${teamId}/league-games`)
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, [teamId]);

  if (!data || (data.upcoming.length === 0 && data.recent.length === 0)) return null;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem('gd.leagueGamesCard.open', next ? '1' : '0'); } catch { /* ignore */ }
  };
  const linkFor = (g) => {
    if (data.can_open_sheets) return { to: `/league/${g.league_id}/game/${g.id}`, external: false };
    if (g.league_is_public && g.league_slug) return { to: `/l/${g.league_slug}/game/${g.id}`, external: true };
    return null;
  };
  const leagueNames = data.leagues.map((l) => l.name).join(' · ');

  const renderRow = (g) => {
    const final = g.is_scoresheet_locked && g.result_home_score != null;
    const us = g.is_home ? g.result_home_score : g.result_away_score;
    const them = g.is_home ? g.result_away_score : g.result_home_score;
    const outcome = final ? (us > them ? 'W' : us < them ? 'L' : 'T') : null;
    const link = linkFor(g);
    const body = (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 10, background: 'var(--surface-2)' }}>
        <div style={{ width: 92, flexShrink: 0, fontSize: 12, color: 'var(--text-2)' }}>
          <div style={{ fontWeight: 700, color: 'var(--text)' }}>{fmtDate(g.game_date)}</div>
          {fmtTime(g.game_time)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {g.is_home ? 'vs' : '@'} {g.opponent_name}
            {g.is_playoff && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--puck)', fontWeight: 800 }}>PLAYOFFS</span>}
          </div>
          {g.venue_name && (
            <div style={{ fontSize: 11, color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <MapPin size={10} /> {g.venue_name}
            </div>
          )}
        </div>
        {final ? (
          <span style={{
            fontWeight: 800, fontSize: 13, whiteSpace: 'nowrap',
            color: outcome === 'W' ? 'var(--goal)' : outcome === 'L' ? 'var(--penalty)' : 'var(--text-2)',
          }}>{outcome} {us}–{them}</span>
        ) : link && data.can_open_sheets ? (
          <span style={{ fontSize: 11, color: 'var(--ice)', fontWeight: 700, whiteSpace: 'nowrap' }}>Game sheet →</span>
        ) : null}
      </div>
    );
    if (!link) return <div key={g.id}>{body}</div>;
    return link.external
      ? <a key={g.id} href={link.to} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>{body}</a>
      : <Link key={g.id} to={link.to} style={{ textDecoration: 'none', color: 'inherit' }}>{body}</Link>;
  };

  return (
    <div className="card" style={{ marginBottom: 14, padding: 14 }}>
      <button type="button" onClick={toggle} style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none',
        padding: 0, cursor: 'pointer', color: 'var(--text)', textAlign: 'left',
      }}>
        <Trophy size={16} color="var(--ice)" />
        <span style={{ fontWeight: 800, fontSize: 14, flex: 1, minWidth: 0 }}>
          League games
          <span style={{ fontWeight: 500, color: 'var(--text-2)', fontSize: 12, marginLeft: 8 }}>{leagueNames}</span>
        </span>
        {open ? <ChevronUp size={16} color="var(--text-2)" /> : <ChevronDown size={16} color="var(--text-2)" />}
      </button>
      {open && (
        <div style={{ marginTop: 10, display: 'grid', gap: 6 }}>
          {data.upcoming.length > 0 && (
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-2)' }}>Next</div>
          )}
          {data.upcoming.map(renderRow)}
          {data.recent.length > 0 && (
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-2)', marginTop: 6 }}>Latest results</div>
          )}
          {data.recent.map(renderRow)}
          {data.leagues.filter((l) => l.is_public && l.slug).map((l) => (
            <a key={l.id} href={`/l/${l.slug}/standings`} target="_blank" rel="noopener noreferrer"
               style={{ fontSize: 12, color: 'var(--ice)', textDecoration: 'none', marginTop: 4 }}>
              {l.name} standings →
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
